import { Fragment, useState, useEffect, useRef } from "react";
import Grid from "@mui/material/Grid";
import DeviceAlertLabsDetails from "./DeviceAlertLabsDetails";
import CircularProgress from "@mui/material/CircularProgress";
import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import DeviceParticleDetails from "./DeviceParticleDetails";
import SensorDetails from "./SensorDetails";
import FleetWebApi from "../Api/FleetWebApi";
import alertLabsApi from "../Api/AlertLabsApi";
import EventData from "./EventData";
import DeviceManagement from "./DeviceManagement";
import proxiableDevices from "../Configuration/ProxiableDevices";
import cellularTypes from "../Configuration/CellularTypes";
import productIds from "../Configuration/ParticleProductIds";
import Images from "./Images";
import NotesDetails from "../Notes/Notes";
import waveformDevices from "../Configuration/WaveformSensors";
import WaterMonitor from "./WaterMonitor";
import { useAuthDataContext, UserRoles } from "../AuthDataProvider";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <Typography
      component="div"
      role="tabpanel"
      hidden={value !== index}
      id={`scrollable-auto-tabpanel-${index}`}
      aria-labelledby={`scrollable-auto-tab-${index}`}
      {...other}
    >
      <Box p={3}>{children}</Box>
    </Typography>
  );
}
export default function DeviceDashboard(props) {
  const { user } = useAuthDataContext();
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [deviceDetails, setDeviceDetails] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [affectedRev, setAffectedRev] = useState(null);
  const [affectedFw, setAffectedFw] = useState(null);
  const [virtualID, setVirtualID] = useState(null);
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const hasNormalizedUrl = useRef(false);

  // Check if we should bypass virtual ID logic
  const bypassVirtual = searchParams.get('bypassVirtual') === 'true';

  function handleChange(event, newValue) {
    setTabValue(newValue);
  }
  
  async function loadDeviceDetails(lookupId) {
    setAffectedFw(null);
    setAffectedRev(null);

    let coreIdToSearch = lookupId;
    let foundVirtualID = null;

    // Skip all virtual ID logic if bypass flag is set
    if (!bypassVirtual) {
      // If virtual id, convert to core ID
      try {
        const hardwareIDs = await alertLabsApi.getHardwareIDFromSensorID(
          lookupId,
          'particle',
          user.token
        );
        // Check if API returned a DIFFERENT value (meaning lookupId was a virtual ID)
        if (hardwareIDs && hardwareIDs[0] && hardwareIDs[0] !== lookupId) {
          // Input was a virtual ID, store it
          foundVirtualID = lookupId;
          coreIdToSearch = hardwareIDs[0];
        }
      } catch (error) {
        // Not a virtual ID, use the original lookupId
      }

      // Try to get virtual ID using the core ID if we haven't found one yet
      if (!foundVirtualID) {
        try {
          const virtualIDResult = await alertLabsApi.getVirtualSensorID(
            coreIdToSearch,
            user.token
          );
          if (
            virtualIDResult &&
            virtualIDResult[0] &&
            virtualIDResult[0] !== coreIdToSearch
          ) {
            foundVirtualID = virtualIDResult[0];
          }
        } catch (error) {
          // No virtual ID available
        }
      }
    }

    setVirtualID(foundVirtualID);

    FleetWebApi.getDeviceDetails(coreIdToSearch)
      .then(async (result) => {
        setDeviceDetails(result);

        // Double-check virtual ID using the device's actual core ID from response (only if not bypassing)
        if (!bypassVirtual) {
          const actualCoreID = result.coreID || result._id;
          
          // Only update virtualID if we haven't found one yet OR if the actualCoreID is different
          if (!foundVirtualID && actualCoreID !== coreIdToSearch) {
            try {
              const virtualIDResult = await alertLabsApi.getVirtualSensorID(
                actualCoreID,
                user.token
              );
              if (
                virtualIDResult &&
                virtualIDResult[0] &&
                virtualIDResult[0] !== actualCoreID
              ) {
                setVirtualID(virtualIDResult[0]);
              }
            } catch (error) {
              // Keep the virtualID we already set
            }
          }
        }
      })
      .catch((error) => {
        setError(error);
      })
      .finally(() => {
        setIsLoaded(true);
      });
  }
  useEffect(() => {
    setIsLoaded(false);
    hasNormalizedUrl.current = false;
    loadDeviceDetails(props.device.split(".").join(""));
  }, [props.device, bypassVirtual]);
  useEffect(() => {
    if (
      deviceDetails &&
      deviceDetails.subtype &&
      deviceDetails.subtype === "Humie"
    ) {
      FleetWebApi.getBadBatchNotes(deviceDetails.subtype).then(
        (result) => {
          setAffectedRev(result.affectedRev);
          setAffectedFw(result.affectedFirmware);
        },
        (error) => {
          console.log(error);
        }
      );
    } else if (deviceDetails && deviceDetails.type) {
      FleetWebApi.getBadBatchNotes(deviceDetails.type).then(
        (result) => {
          setAffectedRev(result.affectedRev);
          setAffectedFw(result.affectedFirmware);
        },
        (error) => {
          console.log(error);
        }
      );
    }
  }, [deviceDetails]);

  // Always update URL to coreID 
  useEffect(() => {
    if (deviceDetails && (deviceDetails.coreID || deviceDetails._id) && !hasNormalizedUrl.current) {
      const coreId = deviceDetails.coreID || deviceDetails._id;
      // Always normalize to core ID (serial, virtual ID, or anything else)
      if (params.id && params.id !== coreId) {
        hasNormalizedUrl.current = true;
        navigate(`/device/${coreId}`, { replace: true });
      }
    }
  }, [deviceDetails, params.id, navigate]);

  let alertLabsDeviceDetailsContainer;
  let particleDeviceDetailsContainer;
  let sensorDetailsContainer;
  let sensorEventsContainer;
  let deviceManagement;
  let NotesContainer;
  let waterMonitor;

  if (error) {
    alertLabsDeviceDetailsContainer = <div>Error: {error.message}</div>;
  } else if (!isLoaded) {
    alertLabsDeviceDetailsContainer = (
      <div>
        <CircularProgress color="secondary" />
      </div>
    );
    if (deviceDetails && cellularTypes.includes(deviceDetails.type)) {
      particleDeviceDetailsContainer = (
        <div>
          <CircularProgress color="secondary" />
        </div>
      );
    }
    sensorEventsContainer = (
      <div>
        <CircularProgress color="secondary" />
      </div>
    );
    deviceManagement = (
      <div>
        <CircularProgress color="secondary" />
      </div>
    );
    NotesContainer = (
      <div>
        <CircularProgress color="secondary" />
      </div>
    );
    waterMonitor = (
      <div>
        <CircularProgress color="secondary" />
      </div>
    );
  } else {
    alertLabsDeviceDetailsContainer = deviceDetails ? (
      <DeviceAlertLabsDetails
        deviceDetails={deviceDetails}
        productId={productIds[deviceDetails.type]}
        affectedFw={affectedFw}
        affectedRev={affectedRev}
      />
    ) : null;

    particleDeviceDetailsContainer = deviceDetails ? (
      cellularTypes.includes(deviceDetails.type) ? (
        <DeviceParticleDetails
          productId={productIds[deviceDetails.type]}
          coreId={
            deviceDetails.coreID ? deviceDetails.coreID : deviceDetails._id
          }
        />
      ) : null
    ) : null;
    if (deviceDetails) {
      sensorEventsContainer = (
        <EventData
          coreId={
            deviceDetails.coreID ? deviceDetails.coreID : deviceDetails._id
          }
          deviceType={deviceDetails.type}
        />
      );
    } else {
      sensorEventsContainer = null;
    }
    if (deviceDetails && cellularTypes.includes(deviceDetails.type)) {
      deviceManagement = (
        <DeviceManagement
          coreId={
            deviceDetails.coreID ? deviceDetails.coreID : deviceDetails._id
          }
          virtualId={virtualID}
          type={deviceDetails.type}
          focused={tabValue === 4}
        />
      );
    } else if (deviceDetails && proxiableDevices.includes(deviceDetails.type)) {
      deviceManagement = (
        <DeviceManagement
          coreId={
            deviceDetails.coreID ? deviceDetails.coreID : deviceDetails._id
          }
          virtualId={virtualID}
          type={deviceDetails.type}
          focused={tabValue === 4}
        />
      );
    } else {
      deviceManagement = null;
    }
    if (deviceDetails) {
      sensorDetailsContainer = (
        <SensorDetails
          coreId={
            deviceDetails.coreID ? deviceDetails.coreID : deviceDetails._id
          }
        />
      );
    } else {
      console.log("error loading sensor details");
      sensorDetailsContainer = null;
    }
    try {
      NotesContainer = (
        <NotesDetails
          Id={deviceDetails.coreID ? deviceDetails.coreID : deviceDetails._id}
          model_type="Device"
        />
      );
    } catch (error) {
      console.log("error loading notes");
    }
    if (
      (user.roles.includes("firmware") || user.roles.includes("support")) &&
      deviceDetails &&
      waveformDevices.includes(deviceDetails.type)
    ) {
      waterMonitor = (
        <WaterMonitor
          coreID={
            deviceDetails.coreID ? deviceDetails.coreID : deviceDetails._id
          }
          virtualID={virtualID}
          focused={tabValue === 7}
          device_type={deviceDetails.type}
        />
      );
    } else {
      waterMonitor = null;
    }
  }
  // Helper to build the device header string
  const getDeviceHeader = () => {
    if (!deviceDetails) return null;
    const serial = deviceDetails._id ?? "";
    const core = deviceDetails.coreID ?? "";
    if (virtualID) {
      return <>
        {serial} - {core} - <span style={{ fontStyle: "italic" }}>({virtualID})</span>
      </>;
    }
    return [serial, core].filter(Boolean).join(" - ");
  };

  return (
    <Fragment>
      {isLoaded && (
        <h1>{getDeviceHeader()}</h1>
      )}

      <AppBar position="static" style={{ background: "#2E3B55" }}>
        <Tabs
          value={tabValue}
          onChange={handleChange}
          variant="scrollable"
          scrollButtons="auto"
          indicatorColor="secondary"
        >
          <Tab
            label="Device Summary"
            disabled={alertLabsDeviceDetailsContainer === null}
          />
          <Tab
            label="Particle Details"
            disabled={
              deviceDetails
                ? cellularTypes.includes(deviceDetails.type)
                  ? false
                  : true
                : true
            }
          />
          <Tab
            label="Sensor Details"
            disabled={sensorDetailsContainer === null}
          />
          <Tab
            label="Device Events"
            disabled={sensorEventsContainer === null}
          />
          <Tab label="Device Management" disabled={deviceManagement === null} />
          <Tab label="Device Photos" disabled={!deviceDetails} />
          <Tab label="Notes" />
          {deviceDetails &&
            waveformDevices.includes(deviceDetails.type) &&
            (user.roles.includes("firmware") ||
              user.roles.includes("support")) && <Tab label="Water Monitor" />}
        </Tabs>
      </AppBar>

      <TabPanel value={tabValue} index={0}>
        <h2>Device</h2>
        {alertLabsDeviceDetailsContainer}
      </TabPanel>
      <TabPanel value={tabValue} index={1}>
        <Grid item>
          <h2>Particle Details</h2>
          {particleDeviceDetailsContainer}
        </Grid>
      </TabPanel>
      <TabPanel value={tabValue} index={2}>
        <Grid item>
          <h2>Sensor Details</h2>
          {sensorDetailsContainer}
        </Grid>
      </TabPanel>
      <TabPanel value={tabValue} index={3}>
        <Grid item>
          <h2>Device Events</h2>
          {sensorEventsContainer}
        </Grid>
      </TabPanel>
      <TabPanel value={tabValue} index={4}>
        <Grid>
          <h2>Device Management</h2>
          {deviceManagement}
        </Grid>
      </TabPanel>
      <TabPanel value={tabValue} index={5}>
        <Images
          coreID={virtualID || (deviceDetails?.coreID ?? deviceDetails?._id)}
          focused={tabValue === 5}
        />
      </TabPanel>
      <TabPanel value={tabValue} index={6}>
        <Grid item>
          <h2>Notes</h2>
          {NotesContainer}
        </Grid>
      </TabPanel>
      <TabPanel value={tabValue} index={7}>
        <Grid item>
          <h2>Water Monitor</h2>
          {waterMonitor}
        </Grid>
      </TabPanel>
    </Fragment>
  );
}
